/**
 * Dynamic municipality roles – types and utilities
 *
 * Phase 1 – User roles implementation (USER_ROLES.md)
 * Defines ResourceKey, ResourcePermissions, RolePermissions, MunicipalityRole,
 * and roleId validation for the municipality_roles Firestore collection.
 *
 * @module municipalityRoles
 */

// ========================================
// RESOURCE KEYS (matches resource matrix in USER_ROLES.md)
// ========================================

export const RESOURCE_KEYS = [
  'news',
  'events',
  'surveys',
  'poi',
  'issues',
  'lostFound',
  'volunteering',
  'council',
  'mayor',
  'committees',
  'tasks',
  'chat',
  'socialMedia',
  'onlinePayments',
  'pushNotifications',
  'analytics',
  'users',
  'municipalitySettings',
] as const;

export type ResourceKey = (typeof RESOURCE_KEYS)[number];

// ========================================
// RESOURCE PERMISSIONS (CRUD per resource)
// ========================================

export interface ResourcePermissions {
  create?: boolean;
  read?: boolean;
  update?: boolean;
  delete?: boolean;
}

export type RolePermissions = Partial<Record<ResourceKey, ResourcePermissions>>;

// ========================================
// ROLE ID FORMAT VALIDATION (Task 1.5)
// Lowercase alphanumeric and underscore only; 1–50 chars
// ========================================

export const ROLE_ID_REGEX = /^[a-z0-9_]{1,50}$/;

/**
 * Validates that a roleId matches the required format.
 *
 * @param roleId - The role identifier to validate
 * @returns true if valid
 */
export function isValidRoleId(roleId: unknown): roleId is string {
  return typeof roleId === 'string' && ROLE_ID_REGEX.test(roleId);
}

/**
 * Validates roleId; throws if invalid.
 *
 * @param roleId - The role identifier to validate
 * @param fieldName - Optional field name for error message
 */
export function assertValidRoleId(roleId: unknown, fieldName = 'roleId'): asserts roleId is string {
  if (!isValidRoleId(roleId)) {
    throw new Error(
      `${fieldName} must be 1–50 characters, lowercase alphanumeric and underscore only (e.g. admin, support)`,
    );
  }
}

// ========================================
// MUNICIPALITY ROLE (Firestore document shape)
// ========================================

/** Firestore Timestamp-like (seconds + nanoseconds); use .toDate() when reading from Firestore */
export interface TimestampLike {
  seconds: number;
  nanoseconds: number;
}

export interface MunicipalityRole {
  id: string;
  name: string;
  description?: string;
  isSystem: boolean;
  isArchived: boolean;
  permissions: RolePermissions;
  createdAt: TimestampLike | Date;
  updatedAt: TimestampLike | Date;
  createdBy?: string;
}

/**
 * Type for creating a new municipality role (id may be generated).
 */
export interface MunicipalityRoleCreateInput {
  id: string;
  name: string;
  description?: string;
  isSystem: boolean;
  isArchived?: boolean;
  permissions: RolePermissions;
  createdBy?: string;
}

/**
 * Type for updating a municipality role (partial).
 */
export interface MunicipalityRoleUpdateInput {
  name?: string;
  description?: string;
  isArchived?: boolean;
  permissions?: RolePermissions;
}

// ========================================
// USER-LEVEL OVERRIDES (users collection)
// ========================================

/**
 * User-level permission overrides. Stored in users.municipalityRoleOverrides.
 * Additive and subtractive; applies only to that user.
 */
export type MunicipalityRoleOverrides = RolePermissions;

// ========================================
// HELPER: Full CRUD for a resource
// ========================================

export const fullCrud = (): ResourcePermissions => ({
  create: true,
  read: true,
  update: true,
  delete: true,
});

export const readOnly = (): ResourcePermissions => ({
  read: true,
});

/** Chat: create and read only (no update/delete per USER_ROLES.md Q2) */
export const chatPermissions = (): ResourcePermissions => ({
  create: true,
  read: true,
});

/** Analytics: read only (per USER_ROLES.md Q3) */
export const analyticsPermissions = (): ResourcePermissions => ({
  read: true,
});

/** Municipality settings: read and update only (no create/delete) */
export const municipalitySettingsPermissions = (): ResourcePermissions => ({
  read: true,
  update: true,
});

// ========================================
// RESOURCE → MUNICIPALITY PERMISSIONS MAPPING (Phase 3.3 – USER_ROLES.md Appendix B)
// ========================================

import type { MunicipalityPermissions } from './superAdmin';

/** Maps ResourceKey to MunicipalityPermissions key */
export const RESOURCE_TO_PERMISSION_KEY: Record<ResourceKey, keyof MunicipalityPermissions> = {
  news: 'canEditNews',
  events: 'canManageEvents',
  surveys: 'canManageSurveys',
  poi: 'canManagePOIs',
  issues: 'canManageIssues',
  lostFound: 'canManageLostAndFound',
  volunteering: 'canManageVolunteering',
  council: 'canManageCouncil',
  mayor: 'canManageMayor',
  committees: 'canManageCommittees',
  tasks: 'canManageTasks',
  chat: 'canAccessChat',
  socialMedia: 'canManageSocialMedia',
  onlinePayments: 'canManageOnlinePayments',
  pushNotifications: 'canManagePushNotifications',
  analytics: 'canViewAnalytics',
  users: 'canManageUsers',
  municipalitySettings: 'canEditMunicipalitySettings',
};

/**
 * Converts RolePermissions (CRUD per resource) to MunicipalityPermissions (flat booleans).
 * Any CRUD action grants the corresponding permission (e.g. news.create OR news.read → canEditNews).
 */
export function rolePermissionsToMunicipalityPermissions(
  rolePerms: RolePermissions | null | undefined
): MunicipalityPermissions {
  const base: MunicipalityPermissions = {
    canEditNews: false,
    canManageEvents: false,
    canManageSurveys: false,
    canManagePOIs: false,
    canManageIssues: false,
    canManageLostAndFound: false,
    canManageVolunteering: false,
    canManageCouncil: false,
    canManageMayor: false,
    canManageCommittees: false,
    canManageTasks: false,
    canAccessChat: false,
    canManageSocialMedia: false,
    canManageOnlinePayments: false,
    canManagePushNotifications: false,
    canViewAnalytics: false,
    canManageUsers: false,
    canEditMunicipalitySettings: false,
  };
  if (!rolePerms) return base;

  for (const [resource, perms] of Object.entries(rolePerms) as [ResourceKey, ResourcePermissions | undefined][]) {
    if (!perms || !RESOURCE_TO_PERMISSION_KEY[resource]) continue;
    const hasAny = perms.create === true || perms.read === true || perms.update === true || perms.delete === true;
    const key = RESOURCE_TO_PERMISSION_KEY[resource];
    if (key) base[key] = hasAny;
  }
  return base;
}

/**
 * Applies user-level overrides to base role permissions.
 * Overrides are additive and subtractive: explicit true/false overrides the base.
 */
export function applyMunicipalityRoleOverrides(
  base: RolePermissions,
  overrides: MunicipalityRoleOverrides | null | undefined
): RolePermissions {
  if (!overrides || Object.keys(overrides).length === 0) return base;

  const result: RolePermissions = { ...base };

  for (const [resource, overridePerms] of Object.entries(overrides) as [ResourceKey, ResourcePermissions | undefined][]) {
    if (!overridePerms) continue;
    const existing = result[resource] ?? {};
    const merged: ResourcePermissions = {
      create: overridePerms.create !== undefined ? overridePerms.create : existing.create,
      read: overridePerms.read !== undefined ? overridePerms.read : existing.read,
      update: overridePerms.update !== undefined ? overridePerms.update : existing.update,
      delete: overridePerms.delete !== undefined ? overridePerms.delete : existing.delete,
    };
    result[resource] = merged;
  }
  return result;
}

/**
 * Legacy fallback when role is missing or archived (Phase 3.6).
 * Admin and moderator get full access; support, accountant, engineer get support-level.
 */
export function getLegacyPermissionsForMunicipalityRole(
  roleId: string | null | undefined
): MunicipalityPermissions {
  const normalized = roleId?.toLowerCase();
  // Admin and moderator: full access
  if (normalized === 'admin' || normalized === 'moderator') {
    return {
      canEditNews: true,
      canManageEvents: true,
      canManageSurveys: true,
      canManagePOIs: true,
      canManageIssues: true,
      canManageLostAndFound: true,
      canManageVolunteering: true,
      canManageCouncil: true,
      canManageMayor: true,
      canManageCommittees: true,
      canManageTasks: true,
      canAccessChat: true,
      canManageSocialMedia: true,
      canManageOnlinePayments: true,
      canManagePushNotifications: true,
      canViewAnalytics: true,
      canManageUsers: true,
      canEditMunicipalitySettings: true,
    };
  }
  // Support, accountant, engineer: support-level (read most; create/update issues, lost & found)
  if (normalized === 'support' || normalized === 'accountant' || normalized === 'engineer') {
    return {
      canEditNews: false,
      canManageEvents: false,
      canManageSurveys: false,
      canManagePOIs: false,
      canManageIssues: true,
      canManageLostAndFound: true,
      canManageVolunteering: false,
      canManageCouncil: false,
      canManageMayor: false,
      canManageCommittees: false,
      canManageTasks: false,
      canAccessChat: false,
      canManageSocialMedia: false,
      canManageOnlinePayments: false,
      canManagePushNotifications: false,
      canViewAnalytics: true,
      canManageUsers: false,
      canEditMunicipalitySettings: false,
    };
  }
  // Unknown or missing role: deny all
  return {
    canEditNews: false,
    canManageEvents: false,
    canManageSurveys: false,
    canManagePOIs: false,
    canManageIssues: false,
    canManageLostAndFound: false,
    canManageVolunteering: false,
    canManageCouncil: false,
    canManageMayor: false,
    canManageCommittees: false,
    canManageTasks: false,
    canAccessChat: false,
    canManageSocialMedia: false,
    canManageOnlinePayments: false,
    canManagePushNotifications: false,
    canViewAnalytics: false,
    canManageUsers: false,
    canEditMunicipalitySettings: false,
  };
}

/**
 * Resolves role permissions + overrides to MunicipalityPermissions shape.
 * Use when role exists and is not archived.
 */
export function resolveMunicipalityPermissions(
  rolePermissions: RolePermissions | null | undefined,
  overrides?: MunicipalityRoleOverrides | null
): MunicipalityPermissions {
  const base = rolePermissions ?? {};
  const merged = applyMunicipalityRoleOverrides(base, overrides);
  return rolePermissionsToMunicipalityPermissions(merged);
}

// ========================================
// ACTION-LEVEL HELPERS (Phase 3.5)
// ========================================

/**
 * Converts MunicipalityPermissions (flat) to RolePermissions (CRUD).
 * Used when deriving CRUD from super admin assignment permissions.
 */
export function municipalityPermissionsToRolePermissions(
  flat: MunicipalityPermissions
): RolePermissions {
  const result: RolePermissions = {};
  for (const [resource, permKey] of Object.entries(RESOURCE_TO_PERMISSION_KEY) as [
    ResourceKey,
    keyof MunicipalityPermissions
  ][]) {
    if (!flat[permKey]) continue;
    if (resource === 'chat') result[resource] = chatPermissions();
    else if (resource === 'analytics') result[resource] = analyticsPermissions();
    else if (resource === 'municipalitySettings') result[resource] = municipalitySettingsPermissions();
    else result[resource] = fullCrud();
  }
  return result;
}

/**
 * Checks if resolved CRUD permits create for a resource.
 */
export function canCreateResource(crud: RolePermissions, resource: ResourceKey): boolean {
  return crud[resource]?.create === true;
}

/**
 * Checks if resolved CRUD permits read for a resource.
 */
export function canReadResource(crud: RolePermissions, resource: ResourceKey): boolean {
  return crud[resource]?.read === true;
}

/**
 * Checks if resolved CRUD permits update for a resource.
 */
export function canUpdateResource(crud: RolePermissions, resource: ResourceKey): boolean {
  return crud[resource]?.update === true;
}

/**
 * Checks if resolved CRUD permits delete for a resource.
 */
export function canDeleteResource(crud: RolePermissions, resource: ResourceKey): boolean {
  return crud[resource]?.delete === true;
}
